import { forecastFinish } from './forecast.js';
export const GOVERNOR_POLICY_VERSION = 'governor-1.1.0';
/**
 * Capacity consumed once per concurrent worker to establish context (reading the
 * codebase, the slice, the conventions) before it produces anything.
 */
export const DEFAULT_SETUP_BURN_PERCENT_PER_WORKER = 1.5;
/**
 * Capacity consumed per *pair* of concurrent workers on coordination and merge
 * rework, amplified when a branch/service collision is detected.
 */
export const DEFAULT_REWORK_BURN_PERCENT_PER_PAIR = 0.3;
/** Baseline policy carried over from the prior DCC decisions. */
export const WEEKLY_FUEL_CEILINGS = [
    {
        maxUsedPercent: 70,
        ceiling: 4,
        rule: 'Under 70% weekly fuel used: at most 4 implementation workers.',
    },
    {
        maxUsedPercent: 80,
        ceiling: 4,
        rule: '70–80% used: at most 4, and only valuable dependency-safe work.',
    },
    {
        maxUsedPercent: 90,
        ceiling: 3,
        rule: '80–90% used: at most 3, preferring completion-critical journeys.',
    },
    {
        maxUsedPercent: 100.0001,
        ceiling: 2,
        rule: 'Above 90% used: at most 2, completion-critical work only.',
    },
];
export function weeklyFuelCeiling(usedPercent) {
    if (usedPercent === null) {
        return {
            maxUsedPercent: 100,
            ceiling: 2,
            rule: 'Weekly fuel is UNKNOWN; the governor falls back to the most conservative ceiling of 2.',
        };
    }
    for (const rule of WEEKLY_FUEL_CEILINGS) {
        if (usedPercent < rule.maxUsedPercent)
            return rule;
    }
    const last = WEEKLY_FUEL_CEILINGS[WEEKLY_FUEL_CEILINGS.length - 1];
    return last;
}
export function evaluateGovernor(input) {
    const ceilingRule = weeklyFuelCeiling(input.weeklyUsedPercent);
    const reasons = [ceilingRule.rule];
    const constraints = [
        { name: 'Weekly fuel ceiling', limit: ceilingRule.ceiling, detail: ceilingRule.rule },
        {
            name: 'Dependency-safe READY routes',
            limit: input.readyPaths,
            detail: 'A worker without an independent READY route cannot contribute.',
        },
        {
            name: 'Human review capacity',
            limit: input.humanReviewCapacity,
            detail: 'Output that nobody can accept is not throughput.',
        },
        {
            name: 'CI capacity',
            limit: input.ciCapacity,
            detail: 'Concurrent builds beyond CI capacity queue instead of finishing.',
        },
    ];
    let hardCap = Math.min(ceilingRule.ceiling, input.readyPaths, Math.max(1, input.humanReviewCapacity), Math.max(1, input.ciCapacity));
    if (input.readyPaths < ceilingRule.ceiling) {
        reasons.push(`Only ${input.readyPaths} dependency-safe READY route(s): workers ${input.readyPaths + 1}+ would idle, so they are not started.`);
    }
    if (input.completionConfidence === 'LOW' || input.completionConfidence === 'UNKNOWN') {
        const focused = Math.max(1, Math.min(hardCap, input.criticalPathReadyPaths));
        if (focused < hardCap) {
            reasons.push('Completion confidence is low: speculative and non-essential foundation work is frozen and effort concentrates on end-to-end completion.');
            hardCap = focused;
        }
        constraints.push({
            name: 'Completion-critical focus',
            limit: focused,
            detail: 'Low confidence restricts work to routes that complete a user journey.',
        });
    }
    if (input.collisionRisk > 0) {
        reasons.push(`${input.collisionRisk} branch/service collision(s) detected between candidate assignments; parallelism is reduced to avoid rework.`);
        hardCap = Math.max(1, hardCap - input.collisionRisk);
        constraints.push({
            name: 'Collision risk',
            limit: input.collisionRisk,
            detail: 'Two workers on the same service path produce merge rework, not throughput.',
        });
    }
    const maxToEvaluate = Math.max(1, Math.min(6, Math.max(hardCap + 1, input.currentWorkers)));
    const options = [];
    let previous = null;
    for (let n = 1; n <= maxToEvaluate; n += 1) {
        const forecast = forecastFinish({
            ...input.forecastBase,
            workers: n,
            readyPaths: input.readyPaths,
        });
        const hours = forecast.totalHours;
        // Burn model (documented and reproducible):
        //   running: every live worker consumes capacity for the whole window,
        //            including the time it spends waiting for review or CI;
        //   setup:   a fixed context-establishment cost per concurrent worker;
        //   rework:  a per-pair coordination and merge-rework cost, amplified by any
        //            detected branch/service collision.
        // The last two terms are why burn grows faster than parallelism pays back.
        const setupBurn = input.setupBurnPercentPerWorker ?? DEFAULT_SETUP_BURN_PERCENT_PER_WORKER;
        const reworkBurn = input.reworkBurnPercentPerPair ?? DEFAULT_REWORK_BURN_PERCENT_PER_PAIR;
        const live = Math.min(n, input.readyPaths);
        const burn = hours === null
            ? null
            : Math.round((input.burnPercentPerWorkerHour * live * hours +
                setupBurn * live +
                reworkBurn * ((live * (live - 1)) / 2) * (1 + input.collisionRisk)) *
                1000) / 1000;
        let gain = null;
        let burnUp = null;
        if (previous && hours !== null && burn !== null && previous.hours > 0 && previous.burn > 0) {
            gain = round4((previous.hours - hours) / previous.hours);
            burnUp = round4((burn - previous.burn) / previous.burn);
        }
        let allowed = true;
        let rejectionReason = null;
        if (n > hardCap) {
            allowed = false;
            rejectionReason =
                n > ceilingRule.ceiling
                    ? `Above the weekly-fuel ceiling of ${ceilingRule.ceiling}.`
                    : n > input.readyPaths
                        ? `Only ${input.readyPaths} dependency-safe READY route(s) exist.`
                        : 'Blocked by review, CI, collision or completion-focus constraints.';
        }
        else if (gain !== null && burnUp !== null && gain < burnUp) {
            allowed = false;
            rejectionReason = `Worker ${n} buys ${(gain * 100).toFixed(0)}% less time but costs ${(burnUp * 100).toFixed(0)}% more capacity.`;
        }
        options.push({
            workers: n,
            finishHours: hours,
            etaAt: forecast.etaAt,
            burnPercent: burn,
            marginalThroughputGain: gain,
            marginalBurnIncrease: burnUp,
            confidence: forecast.confidence,
            allowed,
            rejectionReason,
            journeysPerCapacityUnit: burn && burn > 0 ? round4(1 / burn) : null,
        });
        if (hours !== null && burn !== null)
            previous = { hours, burn };
    }
    const allowedOptions = options.filter((o) => o.allowed);
    const best = allowedOptions.length > 0 ? allowedOptions[allowedOptions.length - 1] : options[0];
    const recommendedWorkers = best ? best.workers : 1;
    const rejected = options.find((o) => o.workers === recommendedWorkers + 1 && !o.allowed);
    if (rejected?.rejectionReason)
        reasons.push(`Worker ${rejected.workers} rejected: ${rejected.rejectionReason}`);
    const dispatchBlockReasons = [];
    if (!input.leaseValid) {
        dispatchBlockReasons.push('No valid orchestrator lease for this scope; dispatch is frozen.');
    }
    if (input.sessionState === 'EXHAUSTED' || input.sessionState === 'RESET_PENDING') {
        dispatchBlockReasons.push('Session range is exhausted; the driver cannot work until it resets.');
    }
    if (input.weeklyState === 'EXHAUSTED') {
        dispatchBlockReasons.push('Weekly fuel is exhausted for this scope.');
    }
    if (input.readyPaths === 0) {
        dispatchBlockReasons.push('No dependency-safe READY work to dispatch.');
    }
    const delta = recommendedWorkers - input.currentWorkers;
    const headline = delta === 0
        ? `Hold at ${recommendedWorkers} worker(s)`
        : delta < 0
            ? `Reduce to ${recommendedWorkers} worker(s)`
            : `Room for ${recommendedWorkers} worker(s)`;
    return {
        recommendedWorkers,
        currentWorkers: input.currentWorkers,
        ceiling: ceilingRule.ceiling,
        ceilingRule: ceilingRule.rule,
        headline,
        reasons,
        constraints,
        options,
        dispatchAllowed: dispatchBlockReasons.length === 0,
        dispatchBlockReasons,
        policyVersion: GOVERNOR_POLICY_VERSION,
        evaluatedAt: input.now,
    };
}
function round4(n) {
    return Math.round(n * 10000) / 10000;
}
//# sourceMappingURL=governor.js.map